import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export function KpiCard({ title, value, delta, deltaType = 'neutral', icon: Icon, variant = 'default', href, ...props }) {
  const navigate = useNavigate();
  const variants = {
    default: 'bg-card',
    success: 'bg-primary/5 border-primary/20',
    warning: 'bg-accent/10 border-accent/30',
    danger: 'bg-destructive/10 border-destructive/30',
  };

  const deltaColors = {
    up: 'text-primary',
    down: 'text-destructive',
    neutral: 'text-muted-foreground',
  };

  const DeltaIcon = (() => {
    if (deltaType === 'up') return TrendingUp;
    if (deltaType === 'down') return TrendingDown;
    return Minus;
  })();

  return (
    <Card
      className={`rounded-xl border shadow-sm p-4 md:p-5 card-interactive ${variants[variant]} ${href ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''}`}
      data-testid={props['data-testid'] || 'kpi-card'}
      onClick={href ? () => navigate(href) : undefined}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {title}
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums break-all">
            {value}
          </p>
          {delta !== undefined && (
            <div className={`mt-1 flex items-center gap-1 text-xs ${deltaColors[deltaType]}`}>
              <DeltaIcon className="h-3 w-3" />
              <span>{delta}</span>
            </div>
          )}
        </div>
        {Icon && (
          <div className="p-2 rounded-lg bg-muted">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
      </div>
    </Card>
  );
}

KpiCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  delta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  deltaType: PropTypes.oneOf(['up', 'down', 'neutral']),
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(['default', 'success', 'warning', 'danger']),
  href: PropTypes.string,
  'data-testid': PropTypes.string,
};
